"use client";

import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

type Scene = "day" | "night";

const AUTOPLAY_MS = 4000;

const SCENES: { id: Scene; icon: typeof Sun; desktop: string; mobile: string }[] = [
  { id: "day", icon: Sun, desktop: "/photos/hero-mattina-desktop.webp", mobile: "/photos/hero-mattina-mobile.webp" },
  { id: "night", icon: Moon, desktop: "/photos/hero-sera-desktop.webp", mobile: "/photos/hero-sera-mobile.webp" },
];

/**
 * Sfondo hero a due scene (mattina/sera) in dissolvenza. Alterna da solo ogni
 * AUTOPLAY_MS su ogni dispositivo; se la persona sceglie a mano con lo switch,
 * l'alternanza si ferma (ha preso il controllo). Con prefers-reduced-motion
 * niente alternanza automatica.
 */
export function HeroMedia() {
  const t = useTranslations("home");
  const [scene, setScene] = useState<Scene>("day");
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    if (!auto) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let timer: number | undefined;

    const schedule = () => {
      window.clearTimeout(timer);
      if (!reduced.matches) {
        timer = window.setTimeout(() => setScene((s) => (s === "day" ? "night" : "day")), AUTOPLAY_MS);
      }
    };

    schedule();
    reduced.addEventListener("change", schedule);
    return () => {
      window.clearTimeout(timer);
      reduced.removeEventListener("change", schedule);
    };
  }, [scene, auto]);

  return (
    <>
      <div className="absolute inset-0 -z-10 bg-inchiostro" aria-hidden="true">
        {SCENES.map(({ id, desktop, mobile }, index) => (
          <picture key={id}>
            <source media="(min-width: 768px)" srcSet={desktop} />
            <img
              src={mobile}
              alt=""
              fetchPriority={index === 0 ? "high" : "low"}
              // loading="lazy" sulla seconda scena: senza, il browser scaricava comunque
              // entrambe le foto (giorno+notte) subito all'apertura, competendo con la
              // foto visibile per banda e peggiorando la LCP — trovato in audit Lighthouse
              // ("Migliora il caricamento delle immagini"), vedi decisions.md.
              loading={index === 0 ? "eager" : "lazy"}
              className={cn(
                "absolute inset-0 h-full w-full object-cover object-bottom transition-opacity duration-700 ease-brand-out motion-reduce:transition-none",
                scene === id ? "opacity-100" : "opacity-0"
              )}
            />
          </picture>
        ))}
        {/* Scrim più leggero di sera: la foto è già scura, non va spenta del tutto. */}
        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-700 ease-brand-out motion-reduce:transition-none",
            scene === "night" ? "opacity-[0.65]" : "opacity-100"
          )}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-inchiostro/80 via-inchiostro/30 to-inchiostro/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-inchiostro/50 via-inchiostro/15 to-transparent" />
        </div>
      </div>

      <div
        role="group"
        aria-label={t("heroSceneGroup")}
        className="absolute top-5 right-5 z-20 flex rounded-sm border border-avorio/25 bg-inchiostro/60 p-0.5 md:top-auto md:right-14 md:bottom-10"
      >
        {SCENES.map(({ id, icon: Icon }) => (
          <button
            key={id}
            type="button"
            aria-pressed={scene === id}
            onClick={() => {
              setAuto(false);
              setScene(id);
            }}
            className={cn(
              "flex min-h-10 items-center gap-2 rounded-[2px] px-3.5 text-xs font-medium tracking-[0.14em] uppercase transition-colors duration-brand-fast focus-visible:ring-2 focus-visible:ring-avorio/70 focus-visible:outline-none md:min-h-9",
              scene === id ? "bg-avorio text-inchiostro" : "text-avorio/75 hover:text-avorio"
            )}
          >
            <Icon className="size-3.5" aria-hidden="true" />
            {id === "day" ? t("heroSceneDay") : t("heroSceneNight")}
          </button>
        ))}
      </div>
    </>
  );
}
